import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import  supabase  from '../../../../supabase-client'; // Asegúrate de tener esto configurado
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import PasswordInput from '../../../components/ui/PasswordInput';
import Icon from '../../../components/AppIcon';
import logo from '/src/assets/favicon.png';
import { reloadPageOnMobile } from '../../../utils/deviceDetection';

const LoginForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email?.trim()) {
      newErrors.email = 'El correo electrónico es requerido';
    } else if (!/\S+@\S+\.\S+/.test(formData?.email)) {
      newErrors.email = 'Ingrese un correo electrónico válido';
    }

    if (!formData?.password?.trim()) {
      newErrors.password = 'La contraseña es requerida';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    try {
      // Validar credenciales en dataadmin (sin Supabase Auth)
      const { data: adminData, error: adminError } = await supabase
        .from('dataadmin')
        .select('id_admin, name_admin, email_admin, password_admin')
        .eq('email_admin', formData.email)
        .eq('password_admin', formData.password)
        .single();

      if (adminError || !adminData) {
        setErrors({ 
          general: 'Credenciales inválidas',
          email: 'Credenciales incorrectas',
          password: 'Credenciales incorrectas'
        });
        return;
      }

      // Establecer sesión en localStorage
      localStorage.setItem('userType', 'admin');
      localStorage.setItem('userName', adminData.name_admin);
      localStorage.setItem('adminId', adminData.id_admin);
      localStorage.setItem('adminEmail', adminData.email_admin);

      navigate('/admin-dashboard-screen');
      
      // Recargar página en dispositivos móviles después del login exitoso
      reloadPageOnMobile();

    } catch (err) {
      console.error('❌ Login error:', err);
      setErrors({ 
        general: 'Error al iniciar sesión. Inténtelo nuevamente.',
        email: 'Error de conexión',
        password: 'Error de conexión'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-card rounded-lg shadow-modal p-6 sm:p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '40px' }}>
            <img src={logo} alt="Logo" style={{ width: '100px', height: 'auto' }} />
          </div>
          <h1 className="text-2xl font-heading font-semibold text-card-foreground mb-2">
            Iniciar Sesión
          </h1>
          <p className="text-muted-foreground">
            Accede a tu cuenta de Administrador
          </p>
        </div>

        {/* General Error */}
        {errors?.general && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-md">
            <div className="flex items-center">
              <Icon name="AlertCircle" size={16} className="text-destructive mr-2" />
              <p className="text-sm text-destructive">{errors?.general}</p>
            </div>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            label="Correo Electrónico"
            type="email"
            name="email"
            placeholder="ejemplo@hotmail.com"
            value={formData?.email}
            onChange={handleInputChange}
            error={errors?.email}
            required
            disabled={isLoading}
          />

          <PasswordInput
            label="Contraseña"
            name="password"
            placeholder="Ingrese su contraseña"
            value={formData?.password}
            onChange={handleInputChange}
            error={errors?.password}
            required
            disabled={isLoading}
          />

          <Button
            type="submit"
            variant="default"
            fullWidth
            loading={isLoading}
            className="bg-accent hover:bg-accent/90 text-accent-foreground"
          >
            {isLoading ? 'Iniciando Sesión...' : 'Iniciar Sesión'}
          </Button>

          <Button
            type="button"
            variant="outline"
            fullWidth
            onClick={() => navigate('/')}
            disabled={isLoading}
            className="mt-3 bg-[#0b3452] hover:bg-[#0b3452]/90 text-white border-[#0b3452]"
          >
            Regresar
          </Button>
        </form>

        {/* Divider */}
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-card text-muted-foreground">Acceso Administrativo</span>
            </div>
          </div>
        </div>

        {/* Admin Info */}
        <div className="mt-8 p-4 bg-muted rounded-md">
          <div className="space-y-2 text-xs text-muted-foreground">
            <center>
              <div className="mt-2 text-xs flex items-center">
                <Icon name="Info" size={12} className="mr-1" />
                <span>Solo el administrador autorizado puede acceder</span>
              </div>
            </center>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
